import { Prisma, type JournalEntry, type PrismaClient } from '@prisma/client';
import { HttpError } from '../../core/errors.js';
import { nextDocumentNumber } from '../../core/sequence.js';
import { CONTROL_ACCOUNT_KEYS } from './chart.js';
import { assertPeriodOpen, toDate } from './periods.js';

type Tx = Prisma.TransactionClient;
type Db = PrismaClient | Prisma.TransactionClient;
const D = (v: Prisma.Decimal.Value) => new Prisma.Decimal(v);

export interface PostingLine {
  accountId?: number;
  /** Resolve the account by its system key (AR, AP, VAT_OUT, ...) instead of an id. */
  systemKey?: string;
  debit?: Prisma.Decimal.Value;
  credit?: Prisma.Decimal.Value;
  memo?: string;
  currency?: string;
  foreignAmount?: Prisma.Decimal.Value;
  exchangeRate?: Prisma.Decimal.Value;
}

export interface PostInput {
  companyId: number;
  /** ISO date `YYYY-MM-DD` (or a Date at UTC midnight). */
  date: string | Date;
  description: string;
  sourceType: string;
  sourceId?: number;
  reference?: string;
  lines: PostingLine[];
  userId?: number;
}

export interface NormalisedLine {
  accountId: number;
  debit: Prisma.Decimal;
  credit: Prisma.Decimal;
  memo?: string;
  currency?: string;
  foreignAmount?: Prisma.Decimal;
  exchangeRate?: Prisma.Decimal;
}

/**
 * Pure validation: at least two lines, each with exactly one positive side, and total debits equal
 * total credits. Throws a 422 HttpError otherwise. Exported for unit tests.
 */
export function assertBalanced(
  lines: { debit?: Prisma.Decimal.Value; credit?: Prisma.Decimal.Value }[],
) {
  if (lines.length < 2)
    throw new HttpError(422, 'UNBALANCED', 'A journal entry needs at least two lines');
  let debit = D(0);
  let credit = D(0);
  lines.forEach((l, i) => {
    const d = D(l.debit ?? 0);
    const c = D(l.credit ?? 0);
    if (d.isNegative() || c.isNegative()) {
      throw new HttpError(422, 'INVALID_LINE', `Line ${i + 1}: amounts cannot be negative`);
    }
    if (d.gt(0) === c.gt(0)) {
      throw new HttpError(
        422,
        'INVALID_LINE',
        `Line ${i + 1}: enter either a debit or a credit, not both or neither`,
      );
    }
    if (d.decimalPlaces() > 4 || c.decimalPlaces() > 4) {
      throw new HttpError(422, 'INVALID_LINE', `Line ${i + 1}: at most 4 decimal places`);
    }
    debit = debit.plus(d);
    credit = credit.plus(c);
  });
  if (!debit.equals(credit)) {
    throw new HttpError(
      422,
      'UNBALANCED',
      `Debits (${debit.toFixed(4)}) must equal credits (${credit.toFixed(4)})`,
    );
  }
  return { total: debit };
}

async function resolveLines(db: Db, input: PostInput): Promise<NormalisedLine[]> {
  const keys = [...new Set(input.lines.map((l) => l.systemKey).filter((k): k is string => !!k))];
  const ids = [...new Set(input.lines.map((l) => l.accountId).filter((i): i is number => !!i))];
  const accounts = await db.account.findMany({
    where: {
      companyId: input.companyId,
      OR: [{ id: { in: ids } }, ...(keys.length ? [{ systemKey: { in: keys } }] : [])],
    },
  });
  return input.lines.map((l, i) => {
    const acc = accounts.find((a) =>
      l.accountId ? a.id === l.accountId : a.systemKey === l.systemKey,
    );
    if (!acc) throw new HttpError(422, 'UNKNOWN_ACCOUNT', `Line ${i + 1}: account not found`);
    if (!acc.isActive)
      throw new HttpError(
        422,
        'ACCOUNT_INACTIVE',
        `Line ${i + 1}: account ${acc.code} is inactive`,
      );
    if (
      input.sourceType === 'manual' &&
      acc.systemKey &&
      CONTROL_ACCOUNT_KEYS.includes(acc.systemKey)
    ) {
      throw new HttpError(
        422,
        'CONTROL_ACCOUNT',
        `Line ${i + 1}: ${acc.name} is a control account maintained by invoices, bills and receipts. Use those documents instead.`,
      );
    }
    return {
      accountId: acc.id,
      debit: D(l.debit ?? 0),
      credit: D(l.credit ?? 0),
      memo: l.memo,
      currency: l.currency,
      foreignAmount: l.foreignAmount === undefined ? undefined : D(l.foreignAmount),
      exchangeRate: l.exchangeRate === undefined ? undefined : D(l.exchangeRate),
    };
  });
}

const asDate = (d: string | Date) => (d instanceof Date ? d : toDate(d));

function lineData(entryId: number, lines: NormalisedLine[]): Prisma.JournalLineCreateManyInput[] {
  return lines.map((l, i) => ({
    entryId,
    accountId: l.accountId,
    lineNo: i + 1,
    debit: l.debit,
    credit: l.credit,
    memo: l.memo?.slice(0, 300),
    currency: l.currency,
    foreignAmount: l.foreignAmount,
    exchangeRate: l.exchangeRate,
  }));
}

/** Saves an editable draft (manual journals). Nothing reaches the ledger until it is posted. */
export async function createDraft(tx: Tx, input: PostInput): Promise<JournalEntry> {
  const lines = await resolveLines(tx, input);
  const { total } = assertBalanced(lines);
  const entry = await tx.journalEntry.create({
    data: {
      companyId: input.companyId,
      entryDate: asDate(input.date),
      description: input.description.slice(0, 500),
      status: 'draft',
      sourceType: input.sourceType,
      sourceId: input.sourceId,
      reference: input.reference,
      totalAmount: total,
      createdBy: input.userId,
    },
  });
  await tx.journalLine.createMany({ data: lineData(entry.id, lines) });
  return entry;
}

export async function updateDraft(
  tx: Tx,
  entryId: number,
  input: PostInput,
): Promise<JournalEntry> {
  const existing = await tx.journalEntry.findFirst({
    where: { id: entryId, companyId: input.companyId },
  });
  if (!existing) throw new HttpError(404, 'NOT_FOUND', 'Journal entry not found');
  if (existing.status !== 'draft') {
    throw new HttpError(
      409,
      'IMMUTABLE',
      'Posted entries cannot be edited. Post a reversal instead.',
    );
  }
  const lines = await resolveLines(tx, input);
  const { total } = assertBalanced(lines);
  await tx.journalLine.deleteMany({ where: { entryId } });
  await tx.journalLine.createMany({ data: lineData(entryId, lines) });
  return tx.journalEntry.update({
    where: { id: entryId },
    data: {
      entryDate: asDate(input.date),
      description: input.description.slice(0, 500),
      reference: input.reference,
      totalAmount: total,
    },
  });
}

/** Turns a draft into a posted (immutable) entry: checks the period, re-checks balance, numbers it. */
export async function postDraft(
  tx: Tx,
  entryId: number,
  companyId: number,
  userId?: number,
): Promise<JournalEntry> {
  const draft = await tx.journalEntry.findFirst({
    where: { id: entryId, companyId },
    include: { lines: true },
  });
  if (!draft) throw new HttpError(404, 'NOT_FOUND', 'Journal entry not found');
  if (draft.status !== 'draft')
    throw new HttpError(409, 'ALREADY_POSTED', 'This entry is already posted');
  assertBalanced(draft.lines);
  await assertPeriodOpen(tx, companyId, draft.entryDate);
  const entryNo = await nextDocumentNumber(tx, companyId, 'journal', draft.entryDate);
  return tx.journalEntry.update({
    where: { id: entryId },
    data: { status: 'posted', entryNo, postedAt: new Date(), postedBy: userId },
  });
}

/**
 * THE posting service (CLAUDE.md rule 2). Every financial document posts through here, inside the
 * caller's transaction: validates, checks the period, numbers the entry and freezes it.
 * Total debits must equal total credits or the whole transaction fails.
 */
export async function postEntry(tx: Tx, input: PostInput): Promise<JournalEntry> {
  await assertPeriodOpen(tx, input.companyId, asDate(input.date));
  const draft = await createDraft(tx, input);
  return postDraft(tx, draft.id, input.companyId, input.userId);
}

/** Corrections are reversals: a new entry with debits and credits swapped, linked to the original. */
export async function reverseEntry(
  tx: Tx,
  args: { companyId: number; entryId: number; reason: string; date?: string; userId?: number },
): Promise<JournalEntry> {
  const original = await tx.journalEntry.findFirst({
    where: { id: args.entryId, companyId: args.companyId },
    include: { lines: { orderBy: { lineNo: 'asc' } } },
  });
  if (!original) throw new HttpError(404, 'NOT_FOUND', 'Journal entry not found');
  if (original.status !== 'posted')
    throw new HttpError(409, 'NOT_POSTED', 'Only posted entries can be reversed');
  if (original.reversedById)
    throw new HttpError(409, 'ALREADY_REVERSED', 'This entry has already been reversed');
  if (original.reversesId)
    throw new HttpError(409, 'IS_REVERSAL', 'A reversal cannot itself be reversed');

  const date = args.date ?? new Date().toISOString().slice(0, 10);
  await assertPeriodOpen(tx, args.companyId, toDate(date));
  const draft = await tx.journalEntry.create({
    data: {
      companyId: args.companyId,
      entryDate: toDate(date),
      description: `Reversal of ${original.entryNo}: ${args.reason}`.slice(0, 500),
      status: 'draft',
      sourceType: 'reversal',
      sourceId: original.id,
      reference: original.entryNo,
      totalAmount: original.totalAmount,
      reversesId: original.id,
      createdBy: args.userId,
    },
  });
  await tx.journalLine.createMany({
    data: original.lines.map((l, i) => ({
      entryId: draft.id,
      accountId: l.accountId,
      lineNo: i + 1,
      debit: l.credit,
      credit: l.debit,
      memo: l.memo,
      currency: l.currency,
      foreignAmount: l.foreignAmount,
      exchangeRate: l.exchangeRate,
    })),
  });
  const posted = await postDraft(tx, draft.id, args.companyId, args.userId);
  await tx.journalEntry.update({ where: { id: original.id }, data: { reversedById: posted.id } });
  return posted;
}

// ───── balances ─────

export interface AccountBalance {
  accountId: number;
  debit: Prisma.Decimal;
  credit: Prisma.Decimal;
}

/** Sum of posted lines per account, optionally bounded by date. */
export async function accountBalances(
  db: Db,
  companyId: number,
  opts: { from?: Date; to?: Date; accountIds?: number[] } = {},
): Promise<AccountBalance[]> {
  const rows = await db.journalLine.groupBy({
    by: ['accountId'],
    where: {
      ...(opts.accountIds ? { accountId: { in: opts.accountIds } } : {}),
      entry: {
        companyId,
        status: 'posted',
        ...(opts.from || opts.to
          ? {
              entryDate: {
                ...(opts.from ? { gte: opts.from } : {}),
                ...(opts.to ? { lte: opts.to } : {}),
              },
            }
          : {}),
      },
    },
    _sum: { debit: true, credit: true },
  });
  return rows.map((r) => ({
    accountId: r.accountId,
    debit: r._sum.debit ?? D(0),
    credit: r._sum.credit ?? D(0),
  }));
}
